/**
 * Receipt Processing Modal Component
 *
 * A modal dialog that displays during receipt upload and OCR processing.
 * Shows progress through different stages:
 * 1. Uploading receipt image
 * 2. Analyzing with AI/OCR
 * 3. Success or error state
 *
 * Features:
 * - Blocks user interaction during processing
 * - Animated spinner with status updates
 * - Auto-closes on success after brief delay
 * - Shows error message with retry option on failure
 */

import { Modal } from './modal';
import { createSpinner } from './spinner';

/**
 * Processing stage enum
 */
export type ProcessingStage = 'uploading' | 'analyzing' | 'success' | 'error';

/**
 * Receipt processing modal options
 */
export interface ReceiptProcessingModalOptions {
  /** Callback when user clicks retry (only shown on error) */
  onRetry?: () => void;
}

/**
 * Stage configuration with icons and messages
 */
interface StageConfig {
  icon: string;
  iconClass: string;
  message: string;
  submessage?: string;
}

const STAGE_CONFIG: Record<ProcessingStage, StageConfig> = {
  uploading: {
    icon: 'upload',
    iconClass: 'text-primary',
    message: 'Uploading receipt...',
    submessage: 'Please wait while we upload your receipt image',
  },
  analyzing: {
    icon: 'scan',
    iconClass: 'text-primary',
    message: 'Analyzing receipt with AI...',
    submessage: 'Extracting vendor, date, amount, and other details',
  },
  success: {
    icon: 'check-circle',
    iconClass: 'text-success',
    message: 'Receipt added successfully',
    submessage: 'All details have been extracted and saved',
  },
  error: {
    icon: 'x-circle',
    iconClass: 'text-destructive',
    message: 'Failed to process receipt',
    submessage: 'An error occurred during processing',
  },
};

/**
 * Receipt Processing Modal class
 */
export class ReceiptProcessingModal {
  private modal: Modal | null = null;
  private currentStage: ProcessingStage = 'uploading';
  private options: ReceiptProcessingModalOptions;
  private contentContainer: HTMLDivElement | null = null;
  private errorMessage: string = '';

  constructor(options: ReceiptProcessingModalOptions = {}) {
    this.options = options;
  }

  /**
   * Open the modal and start showing upload progress
   */
  open(): void {
    if (this.modal !== null) {
      return; // Already open
    }

    this.currentStage = 'uploading';
    this.createModal();
    // createModal() sets this.modal, so we can safely call open()
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.modal!.open();
  }

  /**
   * Update the modal to show analyzing stage
   */
  setAnalyzing(): void {
    this.currentStage = 'analyzing';
    this.updateContent();
  }

  /**
   * Update the modal to show success and auto-close after delay
   */
  async setSuccess(): Promise<void> {
    this.currentStage = 'success';
    this.updateContent();

    // Auto-close after 1.5 seconds
    await new Promise((resolve) => setTimeout(resolve, 1500));
    await this.close();
  }

  /**
   * Update the modal to show error state
   */
  setError(message: string): void {
    this.currentStage = 'error';
    this.errorMessage = message;
    this.updateContent();
  }

  /**
   * Close the modal
   */
  async close(): Promise<void> {
    if (this.modal) {
      await this.modal.close();
      this.modal = null;
      this.contentContainer = null;
    }
  }

  /**
   * Check if modal is currently open
   */
  isOpen(): boolean {
    return this.modal?.getIsOpen() ?? false;
  }

  /**
   * Create the modal instance
   */
  private createModal(): void {
    this.contentContainer = this.buildContent();

    this.modal = new Modal({
      title: 'Processing Receipt',
      content: this.contentContainer,
      size: 'sm',
      closeOnBackdropClick: false,
      closeOnEscape: false,
      showCloseButton: false,
      actions: this.getActions(),
    });
  }

  /**
   * Build the content element for the current stage
   */
  private buildContent(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'receipt-processing-content flex flex-col items-center gap-4 py-6';

    this.renderStageContent(container);

    return container;
  }

  /**
   * Render content for the current stage
   */
  private renderStageContent(container: HTMLElement): void {
    // Clear existing content
    container.innerHTML = '';

    const config = STAGE_CONFIG[this.currentStage];

    // Create icon/spinner container
    const iconContainer = document.createElement('div');
    iconContainer.className = 'flex items-center justify-center';

    if (this.currentStage === 'uploading' || this.currentStage === 'analyzing') {
      // Show animated spinner for processing stages
      const spinner = createSpinner({
        size: 'xl',
        variant: 'primary',
        centered: true,
      });
      iconContainer.appendChild(spinner);
    } else {
      // Show static icon for success/error
      const iconSvg = this.getIconSvg(config.icon);
      iconContainer.innerHTML = `
        <div class="${config.iconClass}">
          ${iconSvg}
        </div>
      `;
    }

    container.appendChild(iconContainer);

    // Main message
    const message = document.createElement('div');
    message.className = 'text-center space-y-2';

    const mainText = document.createElement('p');
    mainText.className = 'text-lg font-semibold text-foreground';
    mainText.textContent = config.message;
    message.appendChild(mainText);

    // Submessage
    const submessage = document.createElement('p');
    submessage.className = 'text-sm text-muted-foreground';
    submessage.textContent = this.currentStage === 'error' && this.errorMessage
      ? this.errorMessage
      : config.submessage || '';
    message.appendChild(submessage);

    container.appendChild(message);
  }

  /**
   * Update the modal content for the current stage
   */
  private updateContent(): void {
    if (!this.contentContainer) return;

    this.renderStageContent(this.contentContainer);

    // Update modal actions based on stage
    if (this.modal) {
      // We need to recreate the modal to update actions
      // This is a limitation of the current Modal implementation
      const wasOpen = this.modal.getIsOpen();
      if (wasOpen) {
        this.modal.close();
        this.createModal();
        this.modal.open();
      }
    }
  }

  /**
   * Get action buttons based on current stage
   */
  private getActions(): Array<{
    label: string;
    variant: 'primary' | 'secondary' | 'destructive' | 'ghost';
    onClick?: () => void | Promise<void>;
  }> {
    if (this.currentStage === 'error') {
      return [
        {
          label: 'Close',
          variant: 'ghost',
          onClick: async () => {
            await this.close();
          },
        },
        ...(this.options.onRetry
          ? [
              {
                label: 'Try Again',
                variant: 'primary' as const,
                onClick: async () => {
                  await this.close();
                  this.options.onRetry?.();
                },
              },
            ]
          : []),
      ];
    }

    // No actions for processing or success stages
    return [];
  }

  /**
   * Get SVG markup for an icon
   */
  private getIconSvg(iconName: string): string {
    const size = 48;
    const strokeWidth = 2;

    switch (iconName) {
      case 'upload':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        `;
      case 'scan':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
            <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
            <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
            <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12" y2="16"/>
          </svg>
        `;
      case 'check-circle':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        `;
      case 'x-circle':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        `;
      default:
        return '';
    }
  }
}

/**
 * Create and open a receipt processing modal
 * Returns the modal instance for control
 */
export function showReceiptProcessingModal(
  options: ReceiptProcessingModalOptions = {}
): ReceiptProcessingModal {
  const modal = new ReceiptProcessingModal(options);
  modal.open();
  return modal;
}
